import React, { useEffect, useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Badge, Button, Tab, Tabs, useMediaQuery } from '@material-ui/core';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import { useSnackbar } from 'notistack';

import ConteinerWithEjecutarPagina from '../../components/UI/ConteinerWithEjecutarPagina/ConteinerWithEjecutarPagina';
import TodasFacturas from '../../components/SmartScan/TodasFacturas/TodasFacturas';
import ListaFacturas from '../../components/SmartScan/ListaFacturas/ListaFacturas';
import DetalleFactura from '../../components/SmartScan/DetalleFactura/DetalleFactura';
import CargaSimulada from '../../components/SmartScan/CargaSimulada/CargaSimulada';
import useSmartScan from '../../components/SmartScan/useSmartScan';
import { descripcionItem, esPendiente, lineasSinAsignar } from '../../components/SmartScan/datosSmartScan';
import colores from '../../components/Impresion/Bandeja/coloresBandeja';

const useStyles = makeStyles((theme) => ({
  pagina: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    height: 'calc(100vh - 87px)',
    minHeight: '560px'
  },
  // En tablet vertical y celular la página crece y se scrollea entera.
  paginaCompacta: {
    height: 'auto',
    minHeight: 0
  },
  encabezado: {
    display: 'flex',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: '12px 16px'
  },
  titulo: {
    fontFamily: 'Righteous, Roboto, sans-serif',
    fontSize: '28px',
    lineHeight: 1,
    color: colores.gris,
    [theme.breakpoints.down('xs')]: {
      fontSize: '22px'
    },
    '& small': {
      display: 'block',
      fontFamily: 'Roboto, sans-serif',
      fontSize: '13px',
      color: colores.secundario,
      marginTop: '6px'
    }
  },
  enDesarrollo: {
    display: 'inline-block',
    verticalAlign: 'middle',
    marginLeft: '10px',
    padding: '3px 10px',
    borderRadius: '12px',
    background: colores.gradiente,
    color: 'white',
    fontFamily: 'Roboto, sans-serif',
    fontSize: '11px',
    fontWeight: 'bold',
    letterSpacing: '0.08em',
    textTransform: 'uppercase'
  },
  subir: {
    marginLeft: 'auto',
    [theme.breakpoints.down('xs')]: {
      width: '100%'
    }
  },
  tabs: {
    backgroundColor: 'white',
    border: `1px solid ${colores.borde}`,
    borderRadius: '6px',
    boxShadow: colores.sombra,
    minHeight: '44px',
    '& .MuiTab-root': {
      minHeight: '44px',
      textTransform: 'none',
      fontSize: '14px'
    },
    '& .MuiBadge-badge': {
      right: '-14px'
    }
  },
  split: {
    flex: 1,
    minHeight: 0,
    display: 'grid',
    gridTemplateColumns: '320px minmax(0, 1fr)',
    gap: '12px',
    [theme.breakpoints.only('md')]: {
      gridTemplateColumns: '280px minmax(0, 1fr)'
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)'
    }
  }
}));

const TABS = { todas: 'todas', revisar: 'revisar', aprobadas: 'aprobadas' };

/**
 * SmartScan (demo): facturas de compra leídas automáticamente del PDF (QR de AFIP, cabecera, líneas y pie), con
 * cada línea imputada a un ítem de compra de Eiffel. Se revisan, se aprueban y quedan grabadas en Eiffel. Todo es
 * ficticio y en memoria: sirve para mostrar la funcionalidad mientras se desarrolla.
 */
const SmartScan = () => {
  sessionStorage.setItem('seccionNombre', 'IA › CapturAI');

  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  // Tablet vertical y celular: se ve la lista o el detalle, no los dos.
  const compacto = useMediaQuery(useTheme().breakpoints.down('sm'));
  const { facturas, quedanPorSubir, asignar, editar, aprobar, reintentar, subir } = useSmartScan();

  const [tab, setTab] = useState(TABS.revisar);
  const [seleccionados, setSeleccionados] = useState({ revisar: null, aprobadas: null });
  const [verDetalle, setVerDetalle] = useState(false);
  const [cargaAbierta, setCargaAbierta] = useState(false);
  const [idNueva, setIdNueva] = useState(null);

  const pendientes = facturas.filter(esPendiente);
  const aprobadas = facturas.filter((factura) => !esPendiente(factura));
  const lista = tab === TABS.aprobadas ? aprobadas : pendientes;
  const elegido = lista.find((factura) => factura.id === seleccionados[tab]) || lista[0] || null;

  const mostrandoDetalle = compacto && verDetalle && tab !== TABS.todas;

  useEffect(() => {
    if (mostrandoDetalle) window.scrollTo(0, 0);
  }, [mostrandoDetalle, elegido && elegido.id]);

  const seleccionar = (pestania, factura) => {
    setSeleccionados((anteriores) => ({ ...anteriores, [pestania]: factura.id }));
    if (compacto) setVerDetalle(true);
  };

  const cambiarTab = (nueva) => {
    setTab(nueva);
    setVerDetalle(false);
  };

  const abrirDesdeTodas = (factura) => {
    const pestania = esPendiente(factura) ? TABS.revisar : TABS.aprobadas;
    setTab(pestania);
    seleccionar(pestania, factura);
  };

  const alAsignar = (grupo, linea, itemCompra) => {
    asignar(elegido.id, grupo, linea.id, itemCompra);
    enqueueSnackbar(`${itemCompra} · ${descripcionItem(itemCompra)}: se va a recordar para las próximas facturas de este proveedor`, {
      variant: 'info'
    });
  };

  const alAprobar = () => {
    const siguiente = pendientes.find((factura) => factura.id !== elegido.id);
    aprobar(elegido.id);
    setSeleccionados((anteriores) => ({ ...anteriores, revisar: siguiente ? siguiente.id : null, aprobadas: elegido.id }));
    if (compacto) setVerDetalle(false);
    enqueueSnackbar(`Factura ${elegido.numero} aprobada: queda en cola para grabarse en Eiffel`, { variant: 'success' });
  };

  const alSubir = () => {
    const nueva = subir();
    setCargaAbierta(false);
    if (!nueva) return;
    setTab(TABS.revisar);
    setSeleccionados((anteriores) => ({ ...anteriores, revisar: nueva.id }));
    setIdNueva(nueva.id);
    if (compacto) setVerDetalle(true);
    const sinAsignar = lineasSinAsignar(nueva);
    enqueueSnackbar(
      sinAsignar === 0
        ? `Factura de ${nueva.proveedor} leída: todas las líneas se imputaron solas`
        : `Factura de ${nueva.proveedor} leída: quedan ${sinAsignar} líneas por imputar`,
      { variant: 'success' }
    );
  };

  return (
    <ConteinerWithEjecutarPagina isExecuted={false}>
      <div className={`${classes.pagina} ${compacto ? classes.paginaCompacta : ''}`}>
        <div className={classes.encabezado}>
          <div className={classes.titulo}>
            SmartScan
            <span className={classes.enDesarrollo}>En desarrollo</span>
            <small>Facturas de compra leídas automáticamente e imputadas a los ítems de compra de Eiffel</small>
          </div>
          <Button
            className={classes.subir}
            color="primary"
            variant="contained"
            startIcon={<CloudUploadOutlinedIcon />}
            onClick={() => setCargaAbierta(true)}
          >
            Subir factura
          </Button>
        </div>

        <Tabs
          className={classes.tabs}
          value={tab}
          onChange={(event, nueva) => cambiarTab(nueva)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value={TABS.todas} label="Todas las facturas" />
          <Tab
            value={TABS.revisar}
            label={
              <Badge badgeContent={pendientes.length} color="secondary">
                Para revisar
              </Badge>
            }
          />
          <Tab value={TABS.aprobadas} label="Aprobadas" />
        </Tabs>

        {tab === TABS.todas ? (
          <TodasFacturas facturas={facturas} onAbrir={abrirDesdeTodas} />
        ) : (
          <div className={classes.split}>
            {!mostrandoDetalle && (
              <ListaFacturas
                facturas={lista}
                seleccionado={elegido && !compacto ? elegido.id : null}
                idNueva={idNueva}
                onSeleccionar={(factura) => seleccionar(tab, factura)}
                mensajeVacio={tab === TABS.revisar ? 'No quedan facturas para revisar' : 'Todavía no hay facturas aprobadas'}
              />
            )}
            {(!compacto || mostrandoDetalle) && (
              <DetalleFactura
                key={elegido ? elegido.id : 'vacio'}
                factura={elegido}
                onVolver={compacto ? () => setVerDetalle(false) : undefined}
                onEditar={(campo, valor) => editar(elegido.id, campo, valor)}
                onAsignar={alAsignar}
                onAprobar={alAprobar}
                onReintentar={() => reintentar(elegido.id)}
              />
            )}
          </div>
        )}
      </div>

      <CargaSimulada abierto={cargaAbierta} quedanPorSubir={quedanPorSubir} onCerrar={() => setCargaAbierta(false)} onSubir={alSubir} />
    </ConteinerWithEjecutarPagina>
  );
};

export default SmartScan;
